/**
 * User Menu Component
 * Sidebar-footer dropdown showing the signed-in user (avatar + name); opens a menu with the
 * email, a Settings entry, a Theme submenu (light/dark/system), and a sign-out action.
 * @packageDocumentation
 */

import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Sun, Moon, LogOut, Monitor, Settings, ChevronsUpDown } from 'lucide-react';

import { Theme } from '@/stores';
import { useTheme } from '@/hooks/useTheme';
import { useAuthStore } from '@/stores/auth';

import {
  DropdownMenu,
  DropdownMenuSub,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from './dropdown-menu';

/**
 * User menu component rendered in the sidebar footer.
 * Shows the signed-in user's avatar and name; opens a dropdown with the email,
 * a Settings entry, a Theme submenu (light/dark/system), and a sign-out action.
 *
 * @returns { JSX.Element } The user menu element.
 */
export const UserMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuthStore();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const userInitial = user?.firstName?.charAt(0)?.toUpperCase() ?? user?.email?.charAt(0)?.toUpperCase() ?? 'U';

  const displayName =
    user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : (user?.email ?? t('user.defaultName'));

  let ThemeIcon = Sun;

  switch (theme) {
    case Theme.Dark:
      ThemeIcon = Moon;
      break;

    case Theme.System:
      ThemeIcon = Monitor;
      break;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all duration-200 ease-out hover:bg-sidebar-accent/40"
          aria-label={t('aria.userMenu')}
        >
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/80 text-xs font-semibold text-primary-foreground">
            {userInitial}
          </span>
          <span className="flex-1 truncate text-sm font-medium text-sidebar-foreground">{displayName}</span>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="top"
        sideOffset={8}
        className="w-[var(--radix-dropdown-menu-trigger-width)]"
      >
        <DropdownMenuLabel className="truncate px-3 py-2.5 text-xs font-normal text-muted-foreground">
          {user?.email ?? t('user.defaultName')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem>
          <Settings className="mr-2 size-4" aria-hidden="true" />
          {t('topbar.settings')}
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <ThemeIcon className="mr-2 size-4" aria-hidden="true" />
            {t('theme.label')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent sideOffset={7}>
            <DropdownMenuItem
              onClick={() => setTheme(Theme.Light)}
              className={theme === Theme.Light ? 'bg-accent' : ''}
            >
              <Sun className="mr-2 size-4" aria-hidden="true" />
              {t('theme.light')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme(Theme.Dark)} className={theme === Theme.Dark ? 'bg-accent' : ''}>
              <Moon className="mr-2 size-4" aria-hidden="true" />
              {t('theme.dark')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setTheme(Theme.System)}
              className={theme === Theme.System ? 'bg-accent' : ''}
            >
              <Monitor className="mr-2 size-4" aria-hidden="true" />
              {t('theme.system')}
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          className="text-destructive focus:bg-destructive/10 focus:text-destructive"
        >
          <LogOut className="mr-2 size-4" aria-hidden="true" />
          {t('actions.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
